import type { StationTrack } from "@riq/api";
import { ApiClient, type BroadcastState, broadcastStateOffline } from "@riq/api";
import { LinearPlaybackEngine } from "@riq/playback";
import type { LinearPlaybackSource } from "@riq/shared";
import type { ModeBadgeKind } from "@riq/ui/types";
import type { WebRtcConnectionStep } from "@riq/webrtc";
import { embedCopy } from "./embed-copy.js";

export type LinearSource = LinearPlaybackSource;

export type EmbedDisplay = {
  title: string;
  speaker: string;
  badge: string;
  badgeKind: ModeBadgeKind;
};

export type EmbedLinearSnapshot = {
  display: EmbedDisplay;
  linearAvailable: boolean;
  isPlaying: boolean;
  isTuningIn: boolean;
  connectionStep: WebRtcConnectionStep | null;
  error: string | null;
  activeSource: LinearSource | null;
  linearTarget: LinearSource | null;
};

/** @deprecated Use EmbedLinearSnapshot — kept for embed consumers. */
export type LinearPlaybackSnapshot = EmbedLinearSnapshot;

function buildDisplay(
  activeSource: LinearSource | null,
  broadcastState: BroadcastState,
  stationTrack: StationTrack | null,
): EmbedDisplay {
  if (activeSource === "station") {
    return {
      title: stationTrack?.title ?? embedCopy.stationPlayer.defaultTitle,
      speaker: stationTrack?.speaker ?? "",
      badge: embedCopy.badge.auto,
      badgeKind: "auto",
    };
  }
  if (broadcastState.isLive) {
    return {
      title: broadcastState.title ?? embedCopy.live.title,
      speaker: broadcastState.speaker ?? (broadcastState.isDemo ? embedCopy.live.demoSubtitle : ""),
      badge: embedCopy.badge.live,
      badgeKind: "live",
    };
  }
  return {
    title: embedCopy.live.title,
    speaker: "",
    badge: embedCopy.badge.offline,
    badgeKind: "offline",
  };
}

function toEmbedSnapshot(engine: LinearPlaybackEngine): EmbedLinearSnapshot {
  const snap = engine.snapshot;
  const linearTarget = engine.resolveLinearTarget();
  const stationTrack = engine.currentStationTrack();
  const displaySource =
    snap.activeSource ??
    (linearTarget === "station" ? "station" : snap.broadcastState.isLive ? "live" : null);

  return {
    display: buildDisplay(displaySource, snap.broadcastState, stationTrack),
    linearAvailable: linearTarget != null,
    isPlaying: snap.activeSource === "live" ? snap.livePlaying : snap.stationPlaying,
    isTuningIn: snap.activeSource === "live" ? snap.liveTuningIn : snap.stationTuningIn,
    connectionStep: snap.connectionStep,
    error: snap.error,
    activeSource: snap.activeSource,
    linearTarget,
  };
}

/** Thin adapter: embed display snapshot over the shared LinearPlaybackEngine. */
export class LinearPlaybackController {
  private readonly engine: LinearPlaybackEngine;
  private readonly listeners = new Set<(snapshot: EmbedLinearSnapshot) => void>();
  private lastEmitKey = "";

  constructor(baseUrl: string) {
    this.engine = new LinearPlaybackEngine(new ApiClient({ baseUrl }), {
      copy: embedCopy.errors,
      stickyEngagedLinearTarget: true,
      logPrefix: "[Riq Embed]",
    });
    this.engine.onChange(() => this.emit());
  }

  get snapshot(): EmbedLinearSnapshot {
    return toEmbedSnapshot(this.engine);
  }

  onChange(callback: (snapshot: EmbedLinearSnapshot) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  start(): void {
    this.engine.start();
  }

  dispose(): void {
    this.engine.dispose();
  }

  async toggleLinear(): Promise<void> {
    await this.engine.toggleLinear();
  }

  private emit(): void {
    const snap = this.snapshot;
    const key = JSON.stringify(snap);
    if (key === this.lastEmitKey) return;
    this.lastEmitKey = key;
    for (const cb of this.listeners) cb(snap);
  }
}
